import React from 'react';
import { Wind, Moon, Play, Headphones, Sparkles, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const MEDITATION_DATA = [
  { id: 1, title: 'Deep Focus', category: 'Work', time: '10 min', icon: <Wind size={24} />, color: 'blue' },
  { id: 2, title: 'Better Sleep', category: 'Night', time: '25 min', icon: <Moon size={24} />, color: 'indigo' },
  { id: 3, title: 'Quick Calm', category: 'Stress', time: '5 min', icon: <Sparkles size={24} />, color: 'emerald' },
  { id: 4, title: 'Morning Zen', category: 'Start', time: '15 min', icon: <Headphones size={24} />, color: 'orange' },
];

const MeditationSection = () => {
  return (
    <div className="space-y-6">
      {/* Featured Header Card */}

      <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-[40px] border-2 border-dashed border-gray-100">
  {/* Pulsing Icon */}
  <div className="relative mb-6">
    <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
    <div className="relative bg-emerald-500 p-5 rounded-full text-white shadow-xl shadow-emerald-500/20">
      <Sparkles size={32} className="animate-pulse" />
    </div>
  </div>

  <h3 className="text-2xl font-black text-gray-900 tracking-tighter italic text-center mb-2">
    Engineering <span className="text-emerald-500">Excellence</span>
  </h3>
  
  <p className="text-gray-400 font-bold text-[10px] uppercase tracking-[0.3em] text-center max-w-xs leading-relaxed">
    We are currently developing this module to enhance your fitness journey.
  </p>

  <div className="mt-8 flex items-center gap-2">
    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" />
  </div>
</div>
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-6 text-white shadow-lg">
        <div className="relative z-10 space-y-2">
          <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
            Daily Recommendation
          </span>
          <h2 className="text-2xl font-bold">Unwind your mind</h2>
          <p className="text-emerald-50 opacity-90 text-sm max-w-[200px]">
            A 15-minute guided session to help you find balance.
          </p>
          <button className="mt-4 bg-white text-emerald-600 px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2 hover:bg-emerald-50 transition-colors">
            <Play size={16} fill="currentColor" />
            Start Now
          </button>
        </div>
        {/* Decorative Circles */}
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 right-0 w-24 h-24 bg-emerald-400/20 rounded-full blur-xl" />
      </section>

      {/* Grid Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center px-1">
          <h2 className="text-xl font-bold text-gray-800">Explore Sessions</h2>
          <button className="text-emerald-600 text-sm font-semibold">See all</button>
        </div>

        {/* Responsive Grid: 2 columns on mobile, 3 on tablet, 4 on desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {MEDITATION_DATA.map((item) => (
            <motion.div
              key={item.id}
              whileHover={{ y: -5 }}
              className="bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col items-center text-center group cursor-pointer hover:shadow-md transition-all"
            >
              <div className={`p-4 rounded-2xl mb-3 transition-colors ${
                item.color === 'blue' ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-100' :
                item.color === 'indigo' ? 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100' :
                item.color === 'emerald' ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100' :
                'bg-orange-50 text-orange-600 group-hover:bg-orange-100'
              }`}>
                {item.icon}
              </div>
              
              <h3 className="font-bold text-gray-900 leading-tight">{item.title}</h3>
              <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mt-1 mb-3">
                {item.category}
              </p>
              
              <div className="flex items-center gap-1 text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                <Clock size={12} />
                <span className="text-[10px] font-medium">{item.time}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Secondary Quick-Pick Section */}
      <div className="bg-gray-900 rounded-3xl p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-white/10 p-3 rounded-2xl text-white">
            <Headphones size={24} />
          </div>
          <div>
            <h4 className="text-white font-bold text-sm">Sleep Soundscapes</h4>
            <p className="text-gray-400 text-xs">84 tracks available</p>
          </div>
        </div>
        <button className="bg-emerald-500 text-white p-2 rounded-xl">
          <Play size={20} fill="currentColor" />
        </button>
      </div>
    </div>
  );
};

export default MeditationSection;